import { useEffect, useState } from 'react';
import { cognitoAuth } from '@/services/auth/cognito';
import { useAuthStore } from '@/store/authStore';
import { usersService } from '@/services/api/users';

// Convert permissions array to Record<string, boolean> if needed
function normalizePermissions(permissions: unknown): Record<string, boolean> {
  if (!permissions) return {};
  if (Array.isArray(permissions)) {
    // Convert ['read', 'write'] to { read: true, write: true }
    return permissions.reduce((acc, perm) => ({ ...acc, [perm]: true }), {} as Record<string, boolean>);
  }
  if (typeof permissions === 'object') {
    return permissions as Record<string, boolean>;
  }
  return {};
}

export function useAuth() {
  const { user, setUser, setToken, setLoading, setTenantModules, setTenantPlan, isAuthenticated, isLoading } = useAuthStore();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const isAuth = await cognitoAuth.checkAuth();

        if (isAuth) {
          const userInfo = await cognitoAuth.getUserInfo();
          const token = await cognitoAuth.getIdToken();

          if (userInfo && token) {
            // Set token first so API calls work
            setToken(token);

            // Try to fetch fresh user data from API
            try {
              const userData = await usersService.getCurrentUser();
              if (userData && userData.id) {
                setUser({
                  id: userData.id,
                  cognitoUserId: userData.cognitoUserId || userInfo.userId,
                  tenantId: userData.tenantId || userInfo.tenantId || '',
                  email: userData.email,
                  firstName: userData.firstName || userInfo.givenName || null,
                  lastName: userData.lastName || userInfo.familyName || null,
                  role: userData.role || (userInfo.role as any) || 'user',
                  status: userData.status || 'active',
                  permissions: normalizePermissions(userData.permissions),
                  lastLoginAt: userData.lastLoginAt || new Date().toISOString(),
                  createdAt: userData.createdAt || '',
                  updatedAt: userData.updatedAt || '',
                  createdBy: null,
                  onboardingCompleted: (userData as any).onboardingCompleted || false,
                } as any);
                // Store tenant modules and plan from API response
                setTenantModules((userData as any).tenantModules || null);
                setTenantPlan((userData as any).tenantPlan || 'trial');
              } else {
                // Fallback to Cognito data if API returns no user
                // Keep existing tenantModules from store (set during login)
                setUser({
                  id: userInfo.userId,
                  cognitoUserId: userInfo.userId,
                  tenantId: userInfo.tenantId || '',
                  email: userInfo.email,
                  firstName: userInfo.givenName || null,
                  lastName: userInfo.familyName || null,
                  role: (userInfo.role as any) || 'user',
                  status: 'active',
                  permissions: {},
                  lastLoginAt: new Date().toISOString(),
                  createdAt: '',
                  updatedAt: '',
                  createdBy: null,
                });
              }
            } catch (apiError: any) {
              // If API returns 401/403, user not authorized — sign out cleanly
              if (apiError?.response?.status === 401 || apiError?.response?.status === 403) {
                console.warn('User not authorized in API, signing out');
                await cognitoAuth.signOut();
                useAuthStore.getState().logout();
                return;
              }
              console.warn('Failed to fetch user from API, using Cognito data:', apiError);
              // Fallback to Cognito data if API fails (network issues, 500, etc.)
              // Keep existing tenantModules from store (set during login)
              setUser({
                id: userInfo.userId,
                cognitoUserId: userInfo.userId,
                tenantId: userInfo.tenantId || '',
                email: userInfo.email,
                firstName: userInfo.givenName || null,
                lastName: userInfo.familyName || null,
                role: (userInfo.role as any) || 'user',
                status: 'active',
                permissions: {},
                lastLoginAt: new Date().toISOString(),
                createdAt: '',
                updatedAt: '',
                createdBy: null,
              });
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
      } finally {
        setLoading(false);
        setIsInitialized(true);
      }
    };

    initAuth();
  }, [setUser, setToken, setLoading, setTenantModules, setTenantPlan]);

  return {
    user,
    isAuthenticated,
    isLoading: isLoading || !isInitialized,
  };
}
